<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { UploadViewDataQuery } from '@/generated/graphql'

type UsageLocation = UploadViewDataQuery['upload']['search'][0]['usageLocations'][0]

const props = defineProps<{
  location: UsageLocation
}>()

const { t } = useI18n()

const issueLocation = computed(() =>
  props.location.__typename === 'IssueUploadUsageLocation' ? props.location : undefined,
)
const issueCommentLocation = computed(() =>
  props.location.__typename === 'IssueCommentUploadUsageLocation' ? props.location : undefined,
)
const stepUploadLocation = computed(() =>
  props.location.__typename === 'StepUploadUsageLocation' ? props.location : undefined,
)
const stepImageLocation = computed(() =>
  props.location.__typename === 'StepImageUploadUsageLocation' ? props.location : undefined,
)
const productLocation = computed(() =>
  props.location.__typename === 'ProductUploadUsageLocation' ? props.location : undefined,
)
const productConfigurationLocation = computed(() =>
  props.location.__typename === 'ProductConfigurationUploadUsageLocation'
    ? props.location
    : undefined,
)
const toolImageLocation = computed(() =>
  props.location.__typename === 'ToolImageUploadUsageLocation' ? props.location : undefined,
)
const bopUploadLocation = computed(() =>
  props.location.__typename === 'BOPUploadUsageLocation' ? props.location : undefined,
)
const iconLocation = computed(() =>
  props.location.__typename === 'IconUploadUsageLocation' ? props.location : undefined,
)
const libraryStepLocation = computed(() => {
  const location = stepUploadLocation.value ?? stepImageLocation.value
  return location && !location.bop ? location : undefined
})
const checkLocation = computed(() =>
  props.location.__typename === 'CheckUploadUsageLocation' ? props.location : undefined,
)
</script>

<template>
  <template v-if="issueLocation">
    {{ t('entity.issue.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'issues',
        params: { issueId: issueLocation.issue.id },
      }"
    >
      {{ issueLocation.issue.name }}
    </router-link>
  </template>

  <template v-if="issueCommentLocation">
    {{ t('entity.issueComment.uploadUsage') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'issues',
        params: { issueId: issueCommentLocation.issue.id },
      }"
    >
      {{ issueCommentLocation.issue.name }}
    </router-link>
  </template>

  <template
    v-if="
      stepUploadLocation &&
      stepUploadLocation.bop &&
      stepUploadLocation.module &&
      stepUploadLocation.step
    "
  >
    {{ t('entity.stepUpload.uploadUsage') }}
    <v-icon icon="navigate_next" />
    {{ t('entity.product.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'bopProcess',
        params: {
          productId: stepUploadLocation.bop.productConfiguration.product.id,
          productConfigId: stepUploadLocation.bop.productConfiguration.id,
          siteId: stepUploadLocation.bop.site.id,
          bopId: stepUploadLocation.bop.id,
        },
      }"
    >
      {{ stepUploadLocation.bop.productConfiguration.product.name }}
      <v-icon icon="navigate_next" />
      {{ stepUploadLocation.bop.productConfiguration.name }}
      <v-icon icon="navigate_next" />
      {{ stepUploadLocation.bop.site.name }}
      <v-icon icon="navigate_next" />
      {{ stepUploadLocation.bop.version }}
    </router-link>
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: stepUploadLocation.bop.productConfiguration.product.id,
          productConfigId: stepUploadLocation.bop.productConfiguration.id,
          siteId: stepUploadLocation.bop.site.id,
          bopId: stepUploadLocation.bop.id,
          moduleId: stepUploadLocation.module.id,
        },
      }"
    >
      {{ stepUploadLocation.module.name }}
    </router-link>
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: stepUploadLocation.bop.productConfiguration.product.id,
          productConfigId: stepUploadLocation.bop.productConfiguration.id,
          siteId: stepUploadLocation.bop.site.id,
          bopId: stepUploadLocation.bop.id,
          moduleId: stepUploadLocation.module.id,
          stepId: stepUploadLocation.step.id,
        },
      }"
    >
      {{ stepUploadLocation.step.name }}
    </router-link>
  </template>

  <template
    v-if="
      stepImageLocation &&
      stepImageLocation.bop &&
      stepImageLocation.module &&
      stepImageLocation.step
    "
  >
    {{ t('entity.stepUpload.singular') }}
    <v-icon icon="navigate_next" />
    {{ t('entity.product.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'bopProcess',
        params: {
          productId: stepImageLocation.bop.productConfiguration.product.id,
          productConfigId: stepImageLocation.bop.productConfiguration.id,
          siteId: stepImageLocation.bop.site.id,
          bopId: stepImageLocation.bop.id,
        },
      }"
    >
      {{ stepImageLocation.bop.productConfiguration.product.name }}
      <v-icon icon="navigate_next" />
      {{ stepImageLocation.bop.productConfiguration.name }}
      <v-icon icon="navigate_next" />
      {{ stepImageLocation.bop.site.name }}
      <v-icon icon="navigate_next" />
      {{ stepImageLocation.bop.version }}
    </router-link>
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: stepImageLocation.bop.productConfiguration.product.id,
          productConfigId: stepImageLocation.bop.productConfiguration.id,
          siteId: stepImageLocation.bop.site.id,
          bopId: stepImageLocation.bop.id,
          moduleId: stepImageLocation.module.id,
        },
      }"
    >
      {{ stepImageLocation.module.name }}
    </router-link>
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: stepImageLocation.bop.productConfiguration.product.id,
          productConfigId: stepImageLocation.bop.productConfiguration.id,
          siteId: stepImageLocation.bop.site.id,
          bopId: stepImageLocation.bop.id,
          moduleId: stepImageLocation.module.id,
          stepId: stepImageLocation.step.id,
        },
      }"
    >
      {{ stepImageLocation.step.name }}
    </router-link>
  </template>

  <template v-if="libraryStepLocation">
    {{ t('entity.stepUpload.uploadUsage') }}
    <v-icon icon="navigate_next" />
    {{ t('view.organization.library.libraryStep') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'libraryStep',
        params: { stepId: libraryStepLocation.step.id },
      }"
    >
      {{ libraryStepLocation.step.name }}
    </router-link>
  </template>

  <template v-if="productLocation">
    {{ t('entity.product.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'products',
      }"
    >
      {{ productLocation.product.name }}
    </router-link>
  </template>

  <template
    v-if="
      productConfigurationLocation && productConfigurationLocation.productConfiguration?.product
    "
  >
    {{ t('entity.productConfiguration.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'productConfigurations',
        params: {
          productId: productConfigurationLocation.productConfiguration.product.id,
        },
      }"
    >
      {{ productConfigurationLocation.productConfiguration.product.name }}
      <v-icon icon="navigate_next" />
      {{ productConfigurationLocation.productConfiguration.name }}
    </router-link>
  </template>

  <template v-if="bopUploadLocation && bopUploadLocation.bop">
    {{ t('entity.bopUpload.uploadUsage') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'bopProcess',
        params: {
          productId: bopUploadLocation.bop.productConfiguration.product.id,
          productConfigId: bopUploadLocation.bop.productConfiguration.id,
          siteId: bopUploadLocation.bop.site.id,
          bopId: bopUploadLocation.bop.id,
        },
      }"
    >
      {{ bopUploadLocation.bop.productConfiguration.product.name }}
      <v-icon icon="navigate_next" />
      {{ bopUploadLocation.bop.productConfiguration.name }}
      <v-icon icon="navigate_next" />
      {{ bopUploadLocation.bop.site.name }}
      <v-icon icon="navigate_next" />
      {{ bopUploadLocation.bop.version }}
    </router-link>
  </template>

  <template v-if="toolImageLocation && toolImageLocation.site">
    {{ t('entity.tool.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'tools',
      }"
    >
      {{ toolImageLocation.site.name }}
      <v-icon icon="navigate_next" />
      {{ toolImageLocation.tool.name }}
    </router-link>
  </template>

  <template v-if="iconLocation && iconLocation.icon">
    {{ t('entity.icon.uploadUsage') }}
    <v-icon icon="navigate_next" />
    <router-link :to="{ name: 'icons' }">
      {{ iconLocation.icon.name }}
    </router-link>
  </template>

  <template v-if="checkLocation && checkLocation.bop && checkLocation.module && checkLocation.step">
    {{ t('entity.check.uploadUsage') }}
    <v-icon icon="navigate_next" />
    {{ t('entity.product.singular') }}
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'bopProcess',
        params: {
          productId: checkLocation.bop.productConfiguration.product.id,
          productConfigId: checkLocation.bop.productConfiguration.id,
          siteId: checkLocation.bop.site.id,
          bopId: checkLocation.bop.id,
        },
      }"
    >
      {{ checkLocation.bop.productConfiguration.product.name }}
      <v-icon icon="navigate_next" />
      {{ checkLocation.bop.productConfiguration.name }}
      <v-icon icon="navigate_next" />
      {{ checkLocation.bop.site.name }}
      <v-icon icon="navigate_next" />
      {{ checkLocation.bop.version }}
    </router-link>
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: checkLocation.bop.productConfiguration.product.id,
          productConfigId: checkLocation.bop.productConfiguration.id,
          siteId: checkLocation.bop.site.id,
          bopId: checkLocation.bop.id,
          moduleId: checkLocation.module.id,
        },
      }"
    >
      {{ checkLocation.module.name }}
    </router-link>
    <v-icon icon="navigate_next" />
    <router-link
      :to="{
        name: 'module',
        params: {
          productId: checkLocation.bop.productConfiguration.product.id,
          productConfigId: checkLocation.bop.productConfiguration.id,
          siteId: checkLocation.bop.site.id,
          bopId: checkLocation.bop.id,
          moduleId: checkLocation.module.id,
          stepId: checkLocation.step.id,
        },
      }"
    >
      {{ checkLocation.step.name }}
    </router-link>
  </template>
</template>

<style scoped lang="scss"></style>
